import { useEffect, useRef, useState } from "react";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { Logo } from "../ui/Logo";
import { SidebarItem } from "../molecules/SidebarItem";
import { SidebarSearchTrigger } from "../molecules/SidebarSearchTrigger";
import { SidebarWorkspaceCard } from "./SidebarWorkspaceCard";
import { SidebarUserRow } from "./SidebarUserRow";
import { navCountLabel } from "./sidebar-helpers";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { useUiStore } from "@/lib/stores/ui";
import { ACCESS, type Access } from "@/features/access/access";
import { useRoleCan } from "@/features/access/use-role-access";
import { useActionItemsCountQuery } from "@/features/action-items/queries";
import {
  UNREAD_COUNT_DISPLAY_CAP,
  useUnreadCountQuery,
} from "@/features/notifications/queries";
import { openGleapChat } from "@/lib/gleap";
import { openBetterBugsWidget } from "@/lib/betterbugs";
import { toast } from "@/toast/useToast";
import {
  Mail,
  Settings,
  CreditCard,
  Bell,
  Shield,
  AlertTriangle,
  ChevronsLeft,
  ChevronsRight,
  FileText,
  Code,
  Link as LinkIcon,
  HelpCircle,
  MessageSquare,
} from "@/icons/Icon";

/**
 * Workspace items — Email Accounts + Action items + Templates + Notifications.
 * Day-to-day work only; anything about the org/account itself (API keys,
 * Partner integration, Audit Logs, Billing, Settings) lives in the second
 * group below the divider (`ORG_NAV_ITEMS`).
 *
 * The remaining legacy keys ('overview' | 'deliverability' | 'recovery' |
 * 'reports' | 'help' | 'inbox' | 'sequences' | 'members') were dead clicks
 * (no routes existed), so they stay dropped. If we re-add any of those
 * screens, restore the key + add a corresponding entry in AppShell's
 * `pathToNavKey`.
 */
export type NavKey =
  | "accounts"
  | "action-items"
  | "templates"
  | "notifications"
  | "audit-logs"
  | "settings"
  | "billing"
  | "api-keys"
  | "partner-integration";

type NavItem = {
  key: NavKey;
  label: string;
  to:
    | "/email-accounts"
    | "/action-items"
    | "/templates"
    | "/notifications"
    | "/audit-logs"
    | "/settings"
    | "/billing"
    | "/api-keys"
    | "/partner-integration";
  icon: React.ReactNode;
  /** (Feature, Op) the destination's data needs; a role-blocked item is not rendered.
   * Omitted for VIEWER-floor destinations (every role) so they don't wait on the profile. */
  access?: Access;
  /** Small pill (e.g. "Coming Soon") — after the label, or on the icon when collapsed. */
  badge?: string;
  /** Short form of `badge` for the collapsed rail, where only ~30px is clear.
   * Required whenever `badge` is longer than about five characters, or the
   * corner pill ellipsises it — see `SidebarItem`'s doc. The full `badge` is
   * still what the collapsed tooltip and accessible name say. */
  badgeShort?: string;
};

const NAV_ITEMS: readonly NavItem[] = [
  { key: "accounts", label: "Email Accounts", to: "/email-accounts", icon: <Mail /> },
  { key: "action-items", label: "Action Items", to: "/action-items", icon: <AlertTriangle /> },
  {
    key: "templates",
    label: "Templates",
    to: "/templates",
    icon: <FileText />,
    badge: "Coming Soon",
    badgeShort: "Soon",
  },
  { key: "notifications", label: "Notifications", to: "/notifications", icon: <Bell /> },
];

/**
 * Everything about the org/account itself, in this order. `billing` is open
 * to every workspace member — only the action surfaces inside the page are
 * OWNER-only, gated per-section there. Audit Logs is admin-only (org
 * owners/admins), same as the backend's `190001`. Partner integration is
 * owner-only, same gate `PartnerIntegrationSection` enforces — hiding the
 * entry avoids linking a non-owner to a page that renders empty.
 */
const ORG_NAV_ITEMS: readonly NavItem[] = [
  { key: "api-keys", label: "API Keys", to: "/api-keys", icon: <Code />, access: ACCESS.apiKeys },
  { key: "partner-integration", label: "Partner Integration", to: "/partner-integration", icon: <LinkIcon />, access: ACCESS.partner },
  { key: "audit-logs", label: "Audit Logs", to: "/audit-logs", icon: <Shield />, access: ACCESS.audit },
  { key: "billing", label: "Billing", to: "/billing", icon: <CreditCard /> },
  { key: "settings", label: "Settings", to: "/settings", icon: <Settings /> },
];

export function Sidebar({
  active = "accounts",
  userName,
  userEmail,
}: {
  active?: NavKey;
  userName?: string;
  userEmail?: string;
}) {
  const navigate = useNavigate();
  const router = useRouter();
  const desktopCollapsed = useUiStore((s) => s.sidebarCollapsed);
  const [narrow, setNarrow] = useState(() => window.matchMedia?.('(max-width: 767px)').matches ?? false);
  useEffect(() => {
    const media = window.matchMedia?.('(max-width: 767px)');
    if (!media) return;
    const update = () => setNarrow(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  // Mobile navigation never overwrites the saved desktop layout.
  const collapsed = narrow || desktopCollapsed;
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);

  // Collapsed and expanded render DIFFERENT toggle buttons, so the one that
  // was clicked unmounts and keyboard focus would fall back to <body>. Carry
  // focus over to the newly mounted toggle — only when the old one had it, so
  // a mouse click doesn't leave a focus ring behind.
  const toggleRef = useRef<HTMLButtonElement>(null);
  const toggleHadFocus = useRef(false);
  const onToggle = () => {
    toggleHadFocus.current = document.activeElement === toggleRef.current;
    toggleSidebar();
  };
  useEffect(() => {
    if (toggleHadFocus.current) toggleRef.current?.focus();
    toggleHadFocus.current = false;
  }, [collapsed]);

  // Role-blocked destinations are removed, not disabled — same static map the
  // API's RoleAccessGuard uses. The server stays the authority.
  const { can } = useRoleCan();
  const navItems = NAV_ITEMS.filter((item) => can(item.access));
  const orgNavItems = ORG_NAV_ITEMS.filter((item) => can(item.access));

  // Count badges. Notifications shows the Topbar bell's unread count — the
  // same query (one shared cache entry) and the same `9+` cap, so the two can
  // never disagree.
  const actionItems = useActionItemsCountQuery().data;
  const unreadCount = useUnreadCountQuery().data?.count;
  const counts: Partial<Record<NavKey, string | undefined>> = {
    "action-items": navCountLabel(actionItems?.total, { lowerBound: actionItems?.truncated }),
    notifications: navCountLabel(unreadCount, { cap: UNREAD_COUNT_DISPLAY_CAP }),
  };

  // Intent prefetch — warm a route's loader on hover/focus so the click lands
  // on an already-primed cache, as `<Link>` would.
  const prefetch = (to: NavItem["to"]) => {
    void router.preloadRoute({ to }).catch(() => {});
  };

  function renderNavItem(item: NavItem) {
    const warm = () => prefetch(item.to);
    const count = counts[item.key];
    const node = (
      <SidebarItem
        icon={item.icon}
        label={item.label}
        badge={item.badge}
        badgeShort={item.badgeShort}
        count={count}
        active={active === item.key}
        collapsed={collapsed}
        onClick={() => void navigate({ to: item.to })}
      />
    );
    return (
      <div key={item.key} onMouseEnter={warm} onFocus={warm} className="flex shrink-0">
        {collapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>{node}</TooltipTrigger>
            <TooltipContent side="right" sideOffset={10}>
              {[item.label, item.badge, count || null].filter(Boolean).join(" · ")}
            </TooltipContent>
          </Tooltip>
        ) : (
          node
        )}
      </div>
    );
  }

  function openFeedback() {
    void openBetterBugsWidget().then((opened) => {
      if (!opened) toast.warn("Feedback isn't available right now. Please try again later.");
    });
  }

  const utilButtons = [
    {
      label: "Feedback",
      tip: "Submit feedback",
      icon: <MessageSquare size={collapsed ? 15 : 13} />,
      onClick: openFeedback,
      helpTrigger: false,
    },
    {
      label: "Help",
      tip: "Help",
      icon: <HelpCircle size={collapsed ? 15 : 13} />,
      onClick: (e: React.MouseEvent<HTMLButtonElement>) => openGleapChat(e.currentTarget),
      helpTrigger: true,
    },
  ].map((b) => {
    const button = (
      <button
        key={b.label}
        type="button"
        onClick={b.onClick}
        aria-label={collapsed ? b.tip : undefined}
        data-help-trigger={b.helpTrigger || undefined}
        className="sidebar-util-btn"
      >
        {b.icon}
        {!collapsed && b.label}
      </button>
    );
    if (!collapsed) return button;
    return (
      <Tooltip key={b.label}>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={10}>
          {b.tip}
        </TooltipContent>
      </Tooltip>
    );
  });

  return (
    <TooltipProvider delayDuration={200} disableHoverableContent>
      <aside className={cn("sidebar", collapsed && "is-collapsed")}>
        <div className="sidebar-brand">
          {narrow ? (
            <div className="sidebar-rail-logo">
              <Logo size={28} markOnly />
            </div>
          ) : collapsed ? (
            // The mark doubles as the expand control: hovering the rail swaps
            // it for the expand chevrons (CSS, `.sidebar:hover`).
            <button
              type="button"
              ref={toggleRef}
              onClick={onToggle}
              aria-label="Expand sidebar"
              aria-pressed
              title="Expand sidebar"
              className="sidebar-rail-logo"
            >
              <span className="sidebar-rail-logo-mark">
                <Logo size={28} markOnly />
              </span>
              <span className="sidebar-rail-logo-expand" aria-hidden>
                <ChevronsRight size={18} />
              </span>
            </button>
          ) : (
            <>
              <Logo size={28} wordmarkScale={1.2} />
              <button
                type="button"
                ref={toggleRef}
                onClick={onToggle}
                aria-label="Collapse sidebar"
                aria-pressed={false}
                title="Collapse sidebar"
                className="sidebar-collapse-btn"
              >
                <ChevronsLeft size={14} />
              </button>
            </>
          )}
        </div>

        <SidebarSearchTrigger collapsed={collapsed} />

        <SidebarWorkspaceCard collapsed={collapsed} />

        <div className="sidebar-scroll">
          <nav aria-label="Primary navigation" className="sidebar-nav">
            {navItems.map(renderNavItem)}
          </nav>
          {orgNavItems.length > 0 && (
            <>
              <div className="sidebar-divider" aria-hidden />
              <nav aria-label="Organisation navigation" className="sidebar-nav">
                {orgNavItems.map(renderNavItem)}
              </nav>
            </>
          )}
        </div>

        <div className="sidebar-util">{utilButtons}</div>

        <SidebarUserRow collapsed={collapsed} userName={userName} userEmail={userEmail} />
      </aside>
    </TooltipProvider>
  );
}
